/**
 * Import Page
 *
 * Wizard for importing operations from CSV files.
 * Steps: Upload -> Column Mapping -> Category Mapping -> Payment Method Mapping -> Preview & Execute
 */

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Button } from '../components/ui';
import { formatDate } from '../utils/formatters';
import { useAccountStore } from '../store/accountStore';
import { useCategoryStore } from '../store/categoryStore';
import { usePaymentMethodStore } from '../store/paymentMethodStore';
import { useProjectStore } from '../store/projectStore';
import { importService } from '../services/import.service';
import { suggestionService } from '../services/suggestion.service';
import { aiConfigService } from '../services/ai-config.service';
import api from '../services/api';
import type {
  ParseCsvResponse,
  ColumnMapping,
  CategoryMapping,
  PaymentMethodMapping,
  ProjectMapping,
  ImportTargetField,
  DateFormat,
  CsvDelimiter,
  ImportOperationPreview,
  DuplicateHandling,
  CategorySuggestion,
  ColumnSuggestion,
  PaymentMethodSuggestion,
  ApiResponse,
} from '@budgira/shared';
import { IMPORT_TARGET_FIELDS, DATE_FORMATS, CSV_DELIMITERS } from '@budgira/shared';

// Extended preview type with suggestion flag
interface ImportOperationPreviewWithSuggestion extends ImportOperationPreview {
  isSuggested?: boolean;
}

type WizardStep = 'upload' | 'columns' | 'categories' | 'paymentMethods' | 'projects' | 'preview';

const ImportPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { accountId } = useParams<{ accountId: string }>();
  const { selectedAccount, selectAccount, accounts, fetchAccounts } = useAccountStore();
  const { flatCategories, categories, fetchCategories } = useCategoryStore();
  const { paymentMethods, fetchPaymentMethods } = usePaymentMethodStore();
  const { projects, fetchProjects } = useProjectStore();

  // Refs to prevent multiple fetches
  const hasFetchedCategories = useRef(false);
  const hasFetchedPaymentMethods = useRef(false);
  const hasFetchedProjects = useRef(false);

  // Build a map of category ID to display name (with hierarchy path)
  const categoryDisplayMap = useMemo(() => {
    const map = new Map<string, string>();
    
    function buildPath(nodes: typeof categories, parentPath: string[] = []): void {
      for (const node of nodes) {
        const currentPath = [...parentPath, node.name];
        map.set(node.id, currentPath.join(' › '));
        if (node.children && node.children.length > 0) {
          buildPath(node.children, currentPath);
        }
      }
    }
    
    buildPath(categories);
    return map;
  }, [categories]);

  // Get sorted categories for dropdown (leaf categories first, sorted by display path)
  const sortedCategoriesForDropdown = useMemo(() => {
    // Filter to only leaf categories (no children) - same behavior as CategorySelector
    const leafCategories = flatCategories.filter(cat => {
      // A category is a leaf if it has no children in the tree
      return !flatCategories.some(other => other.parentId === cat.id);
    });
    
    // Sort by display name for alphabetical ordering
    return leafCategories
      .map(cat => ({
        ...cat,
        displayName: categoryDisplayMap.get(cat.id) || cat.name
      }))
      .sort((a, b) => a.displayName.localeCompare(b.displayName, 'fr'));
  }, [flatCategories, categoryDisplayMap]);

  // Wizard state
  const [currentStep, setCurrentStep] = useState<WizardStep>('upload');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // File state
  const [fileContent, setFileContent] = useState<string>('');
  const [fileName, setFileName] = useState<string>('');

  // Parse result state
  const [parseResult, setParseResult] = useState<ParseCsvResponse | null>(null);

  // Settings state
  const [delimiter, setDelimiter] = useState<CsvDelimiter>(';');
  const [dateFormat, setDateFormat] = useState<DateFormat>('DD/MM/YYYY');
  const [hasHeader, setHasHeader] = useState(true);
  const [skipRows, setSkipRows] = useState(0);
  const [skipAfterRow, setSkipAfterRow] = useState(0);
  const [signedAmounts, setSignedAmounts] = useState(true);

  // Mapping state
  const [columnMapping, setColumnMapping] = useState<ColumnMapping[]>([]);
  const [categoryMapping, setCategoryMapping] = useState<CategoryMapping[]>([]);
  const [paymentMethodMapping, setPaymentMethodMapping] = useState<PaymentMethodMapping[]>([]);
  const [projectMapping, setProjectMapping] = useState<ProjectMapping[]>([]);

  // Preview state
  const [previews, setPreviews] = useState<ImportOperationPreviewWithSuggestion[]>([]);
  const [previewStats, setPreviewStats] = useState({ totalRows: 0, validRows: 0, invalidRows: 0 });

  // Import options
  const [duplicateHandling, setDuplicateHandling] = useState<DuplicateHandling>('import');
  const [autoCreateCategories, setAutoCreateCategories] = useState(false);
  const [autoCreatePaymentMethods, setAutoCreatePaymentMethods] = useState(false);
  const [autoCreateProjects, setAutoCreateProjects] = useState(false);

  // AI Suggestions state
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState<CategorySuggestion[]>([]);
  const [suggestionsApplied, setSuggestionsApplied] = useState(false);
  const [suggestionsEnabled, setSuggestionsEnabled] = useState(false);
  
  // Column Suggestions state
  const [isLoadingColumnSuggestions, setIsLoadingColumnSuggestions] = useState(false);
  const [columnSuggestions, setColumnSuggestions] = useState<ColumnSuggestion[]>([]);
  const [columnSuggestionsApplied, setColumnSuggestionsApplied] = useState(false);

  // Payment Method Suggestions state
  const [isLoadingPaymentMethodSuggestions, setIsLoadingPaymentMethodSuggestions] = useState(false);
  const [paymentMethodSuggestions, setPaymentMethodSuggestions] = useState<PaymentMethodSuggestion[]>([]);
  const [paymentMethodSuggestionsApplied, setPaymentMethodSuggestionsApplied] = useState(false);

  // Import result
  const [importResult, setImportResult] = useState<{
    status: string;
    importedCount: number;
    skippedCount: number;
    errorCount: number;
    skipped?: Array<{
      row: number;
      reason: string;
      data?: { date?: string; amount?: string; counterparty?: string };
    }>;
    errors?: Array<{
      row: number;
      message: string;
    }>;
  } | null>(null);

  // Load account and data
  useEffect(() => {
    if (accounts.length === 0) {
      fetchAccounts();
    }
  }, [accounts.length, fetchAccounts]);

  useEffect(() => {
    if (accountId && accounts.length > 0) {
      const account = accounts.find((a) => a.id === accountId);
      if (account && (!selectedAccount || selectedAccount.id !== accountId)) {
        selectAccount(accountId);
      }
    }
  }, [accountId, accounts, selectedAccount, selectAccount]);

  // Load categories and payment methods only once when accountId is available
  useEffect(() => {
    if (accountId && !hasFetchedCategories.current) {
      hasFetchedCategories.current = true;
      fetchCategories();
    }
  }, [accountId, fetchCategories]);

  useEffect(() => {
    if (accountId && !hasFetchedPaymentMethods.current) {
      hasFetchedPaymentMethods.current = true;
      fetchPaymentMethods();
    }
  }, [accountId, fetchPaymentMethods]);

  useEffect(() => {
    if (accountId && !hasFetchedProjects.current) {
      hasFetchedProjects.current = true;
      fetchProjects();
    }
  }, [accountId, fetchProjects]);

  // Load AI config to check if suggestions are enabled
  useEffect(() => {
    const loadSuggestionsConfig = async () => {
      try {
        // Use the public suggestions-config endpoint (not admin-only)
        const response = await api.get<ApiResponse<{ suggestionsEnabled: boolean; aiEnabled: boolean; levenshteinEnabled: boolean }>>('/import/suggestions-config');
        if (response.data.success && response.data.data) {
          setSuggestionsEnabled(response.data.data.suggestionsEnabled);
        }
      } catch {
        // If we can't load config, disable suggestions
        setSuggestionsEnabled(false);
      }
    };
    loadSuggestionsConfig();
  }, []);

  // Check write permissions - redirect if read-only
  const canWrite = selectedAccount && (!selectedAccount.isShared || selectedAccount.permission === 'write' || selectedAccount.permission === 'owner');
  
  useEffect(() => {
    if (selectedAccount && !canWrite) {
      // Redirect to operations page if user doesn't have write permission
      navigate(`/accounts/${accountId}/operations`, { replace: true });
    }
  }, [selectedAccount, canWrite, accountId, navigate]);

  // Extract unique values from CSV for mapping
  // Extract unique categories from backend's uniqueColumnValues (all rows, not just preview)
  const uniqueCategories = useMemo((): string[] => {
    if (!parseResult || columnMapping.length === 0) return [];
    const categoryCol = columnMapping.find((m) => m.targetField === 'categoryName');
    if (!categoryCol) return [];

    // Use uniqueColumnValues from backend if available (contains all values from file)
    if (parseResult.uniqueColumnValues && parseResult.uniqueColumnValues[categoryCol.sourceColumn]) {
      return parseResult.uniqueColumnValues[categoryCol.sourceColumn] ?? [];
    }

    // Fallback to preview rows if uniqueColumnValues not available
    const values = new Set<string>();
    parseResult.previewRows.forEach((row) => {
      const val = row[categoryCol.sourceColumn];
      if (val && val.trim()) {
        values.add(val.trim());
      }
    });
    return Array.from(values).sort((a, b) => a.localeCompare(b, 'fr', { sensitivity: 'base' }));
  }, [parseResult, columnMapping]);

  // Extract unique payment methods from backend's uniqueColumnValues (all rows, not just preview)
  const uniquePaymentMethods = useMemo((): string[] => {
    if (!parseResult || columnMapping.length === 0) return [];
    const pmCol = columnMapping.find((m) => m.targetField === 'paymentMethodName');
    if (!pmCol) return [];

    // Use uniqueColumnValues from backend if available (contains all values from file)
    if (parseResult.uniqueColumnValues && parseResult.uniqueColumnValues[pmCol.sourceColumn]) {
      return parseResult.uniqueColumnValues[pmCol.sourceColumn] ?? [];
    }

    // Fallback to preview rows if uniqueColumnValues not available
    const values = new Set<string>();
    parseResult.previewRows.forEach((row) => {
      const val = row[pmCol.sourceColumn];
      if (val && val.trim()) {
        values.add(val.trim());
      }
    });
    return Array.from(values).sort((a, b) => a.localeCompare(b, 'fr', { sensitivity: 'base' }));
  }, [parseResult, columnMapping]);

  // Extract unique projects from backend's uniqueColumnValues (all rows, not just preview)
  const uniqueProjects = useMemo((): string[] => {
    if (!parseResult || columnMapping.length === 0) return [];
    const projectCol = columnMapping.find((m) => m.targetField === 'projectName');
    if (!projectCol) return [];

    // Use uniqueColumnValues from backend if available (contains all values from file)
    if (parseResult.uniqueColumnValues && parseResult.uniqueColumnValues[projectCol.sourceColumn]) {
      return parseResult.uniqueColumnValues[projectCol.sourceColumn] ?? [];
    }

    // Fallback to preview rows if uniqueColumnValues not available
    const values = new Set<string>();
    parseResult.previewRows.forEach((row) => {
      const val = row[projectCol.sourceColumn];
      if (val && val.trim()) {
        values.add(val.trim());
      }
    });
    return Array.from(values).sort((a, b) => a.localeCompare(b, 'fr', { sensitivity: 'base' }));
  }, [parseResult, columnMapping]);

  // Handle file upload
  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    console.log('[ImportPage] File selected:', file.name);
    setError(null);
    setIsLoading(true);

    try {
      console.log('[ImportPage] Reading file content...');
      
      // Read file as ArrayBuffer to handle encoding detection
      const buffer = await file.arrayBuffer();
      const bytes = new Uint8Array(buffer);
      
      // Check for UTF-8 BOM
      const hasUtf8Bom = bytes[0] === 0xEF && bytes[1] === 0xBB && bytes[2] === 0xBF;
      
      let text: string;
      
      if (hasUtf8Bom) {
        // File has UTF-8 BOM, use UTF-8
        text = new TextDecoder('utf-8').decode(buffer);
        console.log('[ImportPage] Detected UTF-8 BOM, using UTF-8 encoding');
      } else {
        // Try UTF-8 first
        const utf8Text = new TextDecoder('utf-8').decode(buffer);
        
        // Check for replacement character which indicates invalid UTF-8
        if (utf8Text.includes('\uFFFD')) {
          // UTF-8 failed, try Windows-1252 (common for French CSV files)
          text = new TextDecoder('windows-1252').decode(buffer);
          console.log('[ImportPage] UTF-8 failed, using Windows-1252 encoding');
        } else {
          text = utf8Text;
          console.log('[ImportPage] Using UTF-8 encoding');
        }
      }
      
      setFileContent(text);
      setFileName(file.name);

      console.log('[ImportPage] Calling parseCsv API...');
      // Parse CSV
      const result = await importService.parseCsv({
        content: text,
        hasHeader: true,
      });

      console.log('[ImportPage] Parse result:', result);
      setParseResult(result);
      setDelimiter(result.detectedDelimiter);
      setDateFormat(result.detectedDateFormat);

      // Initialize column mapping
      const initialMapping: ColumnMapping[] = result.headers.map((_, index) => ({
        sourceColumn: index,
        targetField: null,
      }));
      setColumnMapping(initialMapping);
    } catch (err) {
      console.error('[ImportPage] Error parsing CSV:', err);
      setError(err instanceof Error ? err.message : t('import.errors.parseFailed'));
    } finally {
      console.log('[ImportPage] Setting isLoading to false');
      setIsLoading(false);
    }
  }, [t]);

  // Re-parse when settings change
  const handleReparse = useCallback(async () => {
    if (!fileContent) return;

    setError(null);
    setIsLoading(true);

    try {
      const result = await importService.parseCsv({
        content: fileContent,
        delimiter,
        hasHeader,
        skipRows,
        skipAfterRow,
      });

      setParseResult(result);

      // Update column mapping if headers changed
      const initialMapping: ColumnMapping[] = result.headers.map((_, index) => ({
        sourceColumn: index,
        targetField: null,
      }));
      setColumnMapping(initialMapping);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('import.errors.parseFailed'));
    } finally {
      setIsLoading(false);
    }
  }, [fileContent, delimiter, hasHeader, skipRows, skipAfterRow, t]);

  // Handle column mapping change
  const handleColumnMappingChange = useCallback((sourceColumn: number, targetField: ImportTargetField | null) => {
    setColumnMapping((prev) => {
      // Remove this target from other columns
      const updated = prev.map((m) => {
        if (m.sourceColumn === sourceColumn) {
          return { ...m, targetField };
        }
        // If another column had this target, clear it (except for 'ignore')
        if (targetField && targetField !== 'ignore' && m.targetField === targetField) {
          return { ...m, targetField: null };
        }
        return m;
      });
      return updated;
    });
  }, []);

  // Handle category mapping change
  const handleCategoryMappingChange = useCallback((sourceValue: string, targetCategoryId: string | null) => {
    setCategoryMapping((prev) => {
      const existing = prev.find((m) => m.sourceValue === sourceValue);
      if (existing) {
        return prev.map((m) => (m.sourceValue === sourceValue ? { ...m, targetCategoryId } : m));
      }
      return [...prev, { sourceValue, targetCategoryId }];
    });
  }, []);

  // Handle payment method mapping change
  const handlePaymentMethodMappingChange = useCallback((sourceValue: string, targetPaymentMethodId: string | null) => {
    setPaymentMethodMapping((prev) => {
      const existing = prev.find((m) => m.sourceValue === sourceValue);
      if (existing) {
        return prev.map((m) => (m.sourceValue === sourceValue ? { ...m, targetPaymentMethodId } : m));
      }
      return [...prev, { sourceValue, targetPaymentMethodId }];
    });
  }, []);

  // Handle project mapping change
  const handleProjectMappingChange = useCallback((sourceValue: string, targetProjectId: string | null) => {
    setProjectMapping((prev) => {
      const existing = prev.find((m) => m.sourceValue === sourceValue);
      if (existing) {
        return prev.map((m) => (m.sourceValue === sourceValue ? { ...m, targetProjectId } : m));
      }
      return [...prev, { sourceValue, targetProjectId }];
    });
  }, []);

  // Get AI suggestions for category mapping
  const handleGetSuggestions = useCallback(async () => {
    if (uniqueCategories.length === 0) return;

    setIsLoadingSuggestions(true);
    setError(null);

    try {
      const response = await aiConfigService.getCategorySuggestions(uniqueCategories);

      if (response.success && response.data) {
        setSuggestions(response.data.suggestions);

        // Apply suggestions to mapping
        const newMapping: CategoryMapping[] = uniqueCategories.map((sourceValue) => {
          const suggestion = response.data?.suggestions.find(
            (s) => s.sourceCategory === sourceValue
          );

          return {
            sourceValue,
            targetCategoryId: suggestion?.suggestedCategoryId || null,
          };
        });

        setCategoryMapping(newMapping);
        setSuggestionsApplied(true);
      } else {
        setError(response.error?.message || t('import.categories.suggestionError', 'Erreur lors des suggestions'));
      }
    } catch (err) {
      console.error('Failed to get suggestions:', err);
      setError(t('import.categories.suggestionError', 'Erreur lors des suggestions'));
    } finally {
      setIsLoadingSuggestions(false);
    }
  }, [uniqueCategories, t]);

  // Get suggestions for column mapping
  const handleGetColumnSuggestions = useCallback(async () => {
    if (!parseResult?.headers || parseResult.headers.length === 0) return;

    setIsLoadingColumnSuggestions(true);
    setError(null);

    try {
      // Filter out empty headers
      const validHeaders = parseResult.headers.filter(h => h && h.trim() !== '');
      const response = await aiConfigService.getColumnSuggestions(validHeaders);

      if (response.success && response.data) {
        setColumnSuggestions(response.data.suggestions);

        // Apply suggestions to mapping
        const newMapping: ColumnMapping[] = parseResult.headers.map((header, index) => {
          if (!header || header.trim() === '') {
            return { sourceColumn: index, targetField: null };
          }
          
          const suggestion = response.data?.suggestions.find(
            (s) => s.sourceColumn === header
          );

          return {
            sourceColumn: index,
            targetField: (suggestion?.suggestedField as ImportTargetField) || null,
          };
        });

        setColumnMapping(newMapping);
        setColumnSuggestionsApplied(true);
      } else {
        setError(response.error?.message || t('import.columns.suggestionError', 'Erreur lors des suggestions'));
      }
    } catch (err) {
      console.error('Failed to get column suggestions:', err);
      setError(t('import.columns.suggestionError', 'Erreur lors des suggestions'));
    } finally {
      setIsLoadingColumnSuggestions(false);
    }
  }, [parseResult, t]);

  // Get suggestions for payment method mapping
  const handleGetPaymentMethodSuggestions = useCallback(async () => {
    if (uniquePaymentMethods.length === 0) return;

    setIsLoadingPaymentMethodSuggestions(true);
    setError(null);

    try {
      const response = await aiConfigService.getPaymentMethodSuggestions(uniquePaymentMethods);

      if (response.success && response.data) {
        setPaymentMethodSuggestions(response.data.suggestions);

        // Apply suggestions to mapping
        const newMapping: PaymentMethodMapping[] = uniquePaymentMethods.map((sourceValue) => {
          const suggestion = response.data?.suggestions.find(
            (s) => s.sourcePaymentMethod === sourceValue
          );
          return {
            sourceValue,
            targetPaymentMethodId: suggestion?.suggestedPaymentMethodId || null,
          };
        });

        setPaymentMethodMapping(newMapping);
        setPaymentMethodSuggestionsApplied(true);
      } else {
        setError(response.error?.message || t('import.paymentMethods.suggestionError', 'Erreur lors des suggestions'));
      }
    } catch (err) {
      console.error('Failed to get payment method suggestions:', err);
      setError(t('import.paymentMethods.suggestionError', 'Erreur lors des suggestions'));
    } finally {
      setIsLoadingPaymentMethodSuggestions(false);
    }
  }, [uniquePaymentMethods, t]);

  // Navigate to next step
  const handleNext = useCallback(async () => {
    setError(null);

    if (currentStep === 'upload') {
      if (!parseResult) {
        setError(t('import.errors.noFile'));
        return;
      }
      setCurrentStep('columns');
    } else if (currentStep === 'columns') {
      // Validate required fields
      const hasDate = columnMapping.some((m) => m.targetField === 'paymentDate');
      const hasAmount = columnMapping.some((m) => m.targetField === 'amount');
      const hasCounterparty = columnMapping.some((m) => m.targetField === 'counterpartyName');

      if (!hasDate || !hasAmount || !hasCounterparty) {
        setError(t('import.errors.missingRequiredFields'));
        return;
      }

      // Check if we have category column mapped
      const hasCategoryCol = columnMapping.some((m) => m.targetField === 'categoryName');
      if (hasCategoryCol && uniqueCategories.length > 0) {
        // Initialize category mapping
        setCategoryMapping(uniqueCategories.map((v) => ({ sourceValue: v, targetCategoryId: null })));
        setCurrentStep('categories');
      } else {
        // Skip to payment methods
        const hasPmCol = columnMapping.some((m) => m.targetField === 'paymentMethodName');
        if (hasPmCol && uniquePaymentMethods.length > 0) {
          setPaymentMethodMapping(uniquePaymentMethods.map((v) => ({ sourceValue: v, targetPaymentMethodId: null })));
          setCurrentStep('paymentMethods');
        } else {
          // Check for projects
          const hasProjectCol = columnMapping.some((m) => m.targetField === 'projectName');
          if (hasProjectCol && uniqueProjects.length > 0) {
            setProjectMapping(uniqueProjects.map((v) => ({ sourceValue: v, targetProjectId: null })));
            setCurrentStep('projects');
          } else {
            // Skip to preview
            await loadPreview();
            setCurrentStep('preview');
          }
        }
      }
    } else if (currentStep === 'categories') {
      const hasPmCol = columnMapping.some((m) => m.targetField === 'paymentMethodName');
      if (hasPmCol && uniquePaymentMethods.length > 0) {
        setPaymentMethodMapping(uniquePaymentMethods.map((v) => ({ sourceValue: v, targetPaymentMethodId: null })));
        setCurrentStep('paymentMethods');
      } else {
        // Check for projects
        const hasProjectCol = columnMapping.some((m) => m.targetField === 'projectName');
        if (hasProjectCol && uniqueProjects.length > 0) {
          setProjectMapping(uniqueProjects.map((v) => ({ sourceValue: v, targetProjectId: null })));
          setCurrentStep('projects');
        } else {
          await loadPreview();
          setCurrentStep('preview');
        }
      }
    } else if (currentStep === 'paymentMethods') {
      // Check for projects
      const hasProjectCol = columnMapping.some((m) => m.targetField === 'projectName');
      if (hasProjectCol && uniqueProjects.length > 0) {
        setProjectMapping(uniqueProjects.map((v) => ({ sourceValue: v, targetProjectId: null })));
        setCurrentStep('projects');
      } else {
        await loadPreview();
        setCurrentStep('preview');
      }
    } else if (currentStep === 'projects') {
      await loadPreview();
      setCurrentStep('preview');
    }
  }, [currentStep, parseResult, columnMapping, uniqueCategories, uniquePaymentMethods, uniqueProjects, t]);

  // Load preview
  const loadPreview = useCallback(async () => {
    if (!accountId || !fileContent) return;

    setIsLoading(true);
    try {
      const result = await importService.previewImport(accountId, {
        content: fileContent,
        columnMapping,
        categoryMapping,
        paymentMethodMapping,
        projectMapping,
        delimiter,
        dateFormat,
        hasHeader,
        skipRows,
        skipAfterRow,
        signedAmounts,
      });

      // Collect counterparty names that don't have a category assigned
      const counterpartiesWithoutCategory = result.previews
        .filter((p) => !p.categoryId && p.counterpartyName)
        .map((p) => p.counterpartyName!);

      // Get unique counterparty names
      const uniqueCounterparties = [...new Set(counterpartiesWithoutCategory)];

      // Fetch category suggestions for these counterparties
      let suggestions: Record<string, { categoryId: string; categoryName: string } | null> = {};
      if (uniqueCounterparties.length > 0) {
        try {
          suggestions = await suggestionService.batchSuggestCategories(uniqueCounterparties, undefined, accountId);
        } catch {
          // Silently fail - suggestions are optional
          console.warn('Failed to fetch category suggestions');
        }
      }

      // Enrich previews with suggested categories
      const enrichedPreviews = result.previews.map((preview) => {
        if (!preview.categoryId && preview.counterpartyName) {
          const suggestion = suggestions[preview.counterpartyName.toLowerCase().trim()];
          if (suggestion) {
            return {
              ...preview,
              categoryId: suggestion.categoryId,
              categoryName: suggestion.categoryName,
              isSuggested: true, // Mark as suggested for UI indication
            };
          }
        }
        return preview;
      });

      setPreviews(enrichedPreviews);
      setPreviewStats({
        totalRows: result.totalRows,
        validRows: result.validRows,
        invalidRows: result.invalidRows,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('import.errors.previewFailed'));
    } finally {
      setIsLoading(false);
    }
  }, [accountId, fileContent, columnMapping, categoryMapping, paymentMethodMapping, delimiter, dateFormat, hasHeader, skipRows, skipAfterRow, signedAmounts, t]);

  // Navigate to previous step
  const handleBack = useCallback(() => {
    if (currentStep === 'columns') {
      setCurrentStep('upload');
    } else if (currentStep === 'categories') {
      setCurrentStep('columns');
    } else if (currentStep === 'paymentMethods') {
      const hasCategoryCol = columnMapping.some((m) => m.targetField === 'categoryName');
      if (hasCategoryCol && uniqueCategories.length > 0) {
        setCurrentStep('categories');
      } else {
        setCurrentStep('columns');
      }
    } else if (currentStep === 'projects') {
      const hasPmCol = columnMapping.some((m) => m.targetField === 'paymentMethodName');
      if (hasPmCol && uniquePaymentMethods.length > 0) {
        setCurrentStep('paymentMethods');
      } else {
        const hasCategoryCol = columnMapping.some((m) => m.targetField === 'categoryName');
        if (hasCategoryCol && uniqueCategories.length > 0) {
          setCurrentStep('categories');
        } else {
          setCurrentStep('columns');
        }
      }
    } else if (currentStep === 'preview') {
      const hasProjectCol = columnMapping.some((m) => m.targetField === 'projectName');
      if (hasProjectCol && uniqueProjects.length > 0) {
        setCurrentStep('projects');
      } else {
        const hasPmCol = columnMapping.some((m) => m.targetField === 'paymentMethodName');
        if (hasPmCol && uniquePaymentMethods.length > 0) {
          setCurrentStep('paymentMethods');
        } else {
          const hasCategoryCol = columnMapping.some((m) => m.targetField === 'categoryName');
          if (hasCategoryCol && uniqueCategories.length > 0) {
            setCurrentStep('categories');
          } else {
            setCurrentStep('columns');
          }
        }
      }
    }
  }, [currentStep, columnMapping, uniqueCategories, uniquePaymentMethods, uniqueProjects]);

  // Execute import
  const handleImport = useCallback(async () => {
    if (!accountId || !fileContent) return;

    setIsLoading(true);
    setError(null);

    try {
      const result = await importService.executeImport(accountId, {
        content: fileContent,
        columnMapping,
        categoryMapping,
        paymentMethodMapping,
        projectMapping,
        delimiter,
        dateFormat,
        hasHeader,
        skipRows,
        skipAfterRow,
        signedAmounts,
        duplicateHandling,
        autoCreateCategories,
        autoCreatePaymentMethods,
        autoCreateProjects,
      });

      setImportResult({
        status: result.status,
        importedCount: result.importedCount,
        skippedCount: result.skippedCount,
        errorCount: result.errorCount,
        skipped: result.skipped,
        errors: result.errors,
      });
      
      // Refresh balance after successful import
      if (result.importedCount > 0) {
        useAccountStore.getState().fetchBalance(accountId);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('import.errors.importFailed'));
    } finally {
      setIsLoading(false);
    }
  }, [
    accountId,
    fileContent,
    columnMapping,
    categoryMapping,
    paymentMethodMapping,
    projectMapping,
    delimiter,
    dateFormat,
    hasHeader,
    skipRows,
    skipAfterRow,
    signedAmounts,
    duplicateHandling,
    autoCreateCategories,
    autoCreatePaymentMethods,
    autoCreateProjects,
    t,
  ]);

  // Step indicator
  const steps: { key: WizardStep; labelKey: string }[] = [
    { key: 'upload', labelKey: 'import.steps.upload' },
    { key: 'columns', labelKey: 'import.steps.columns' },
    { key: 'categories', labelKey: 'import.steps.categories' },
    { key: 'paymentMethods', labelKey: 'import.steps.paymentMethods' },
    { key: 'projects', labelKey: 'import.steps.projects' },
    { key: 'preview', labelKey: 'import.steps.preview' },
  ];

  const currentStepIndex = steps.findIndex((s) => s.key === currentStep);

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 'upload':
        return (
          <div className="space-y-6">
            {/* File input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('import.upload.selectFile')}
              </label>
              {/* Mobile: stacked layout with full-width button */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                <label className="inline-flex items-center justify-center px-4 py-2 rounded-md text-sm font-semibold bg-indigo-50 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-800 cursor-pointer transition-colors">
                  {t('import.upload.chooseFile', 'Choisir un fichier')}
                  <input
                    type="file"
                    accept=".csv,.txt"
                    onChange={handleFileChange}
                    className="sr-only"
                  />
                </label>
                <span className="text-sm text-gray-500 dark:text-gray-400 truncate">
                  {fileName || t('import.upload.noFileSelected', 'Aucun fichier sélectionné')}
                </span>
              </div>
              {fileName && (
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 break-all">
                  {t('import.upload.selectedFile')}: {fileName}
                </p>
              )}
            </div>

            {/* Settings */}
            {parseResult && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('import.upload.delimiter')}
                  </label>
                  <select
                    value={delimiter}
                    onChange={(e) => setDelimiter(e.target.value as CsvDelimiter)}
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
                  >
                    {CSV_DELIMITERS.map((d) => (
                      <option key={d} value={d}>
                        {d === ';' ? t('import.delimiters.semicolon') : d === ',' ? t('import.delimiters.comma') : d === '\t' ? t('import.delimiters.tab') : t('import.delimiters.pipe')}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('import.upload.dateFormat')}
                  </label>
                  <select
                    value={dateFormat}
                    onChange={(e) => setDateFormat(e.target.value as DateFormat)}
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
                  >
                    {DATE_FORMATS.map((f) => (
                      <option key={f} value={f}>
                        {f}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('import.upload.skipRows')}
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={skipRows}
                    onChange={(e) => setSkipRows(Math.max(0, parseInt(e.target.value) || 0))}
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {t('import.upload.skipRowsHint')}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('import.upload.skipAfterRow', 'Ignorer après la ligne')}
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={skipAfterRow}
                    onChange={(e) => setSkipAfterRow(Math.max(0, parseInt(e.target.value) || 0))}
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {t('import.upload.skipAfterRowHint', '0 = pas de limite. Sinon, arrêter l\'import après ce numéro de ligne.')}
                  </p>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="hasHeader"
                    checked={hasHeader}
                    onChange={(e) => setHasHeader(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <label htmlFor="hasHeader" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                    {t('import.upload.hasHeader')}
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="signedAmounts"
                    checked={signedAmounts}
                    onChange={(e) => setSignedAmounts(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <label htmlFor="signedAmounts" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                    {t('import.upload.signedAmounts')}
                  </label>
                </div>
              </div>
            )}

            {/* Preview table */}
            {parseResult && (
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('import.upload.preview')} ({parseResult.totalRows} {t('import.upload.rows')})
                </h3>
                
                {/* Desktop: Table view */}
                <div className="hidden sm:block border rounded-lg dark:border-gray-600 overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        {parseResult.headers.map((header, i) => (
                          <th key={i} className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                      {parseResult.previewRows.slice(0, 5).map((row, i) => (
                        <tr key={i}>
                          {row.map((cell, j) => (
                            <td key={j} className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap max-w-[200px] truncate">
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile: Card view */}
                <div className="sm:hidden space-y-3">
                  {parseResult.previewRows.slice(0, 3).map((row, rowIndex) => (
                    <div key={rowIndex} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                      <div className="text-xs text-gray-400 dark:text-gray-500 mb-2">Ligne {rowIndex + 1}</div>
                      <div className="space-y-1">
                        {parseResult.headers.map((header, colIndex) => (
                          <div key={colIndex} className="flex justify-between gap-2">
                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 truncate flex-shrink-0 max-w-[40%]">
                              {header}
                            </span>
                            <span className="text-sm text-gray-900 dark:text-gray-100 truncate text-right">
                              {row[colIndex] || '-'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  {parseResult.previewRows.length > 3 && (
                    <p className="text-xs text-gray-400 dark:text-gray-500 text-center">
                      +{parseResult.previewRows.length - 3} {t('import.upload.rows')}...
                    </p>
                  )}
                </div>
              </div>
            )}

            <Button onClick={handleReparse} variant="secondary" disabled={!fileContent || isLoading}>
              {t('import.upload.reparse')}
            </Button>
          </div>
        );

      case 'columns':
        // Get list of already selected fields (except 'ignore' which can be used multiple times)
        const usedFields = columnMapping
          .filter((m) => m.targetField && m.targetField !== 'ignore')
          .map((m) => m.targetField);

        return (
          <div className="space-y-4">
            {/* Mobile: stacked layout */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <p className="text-sm text-gray-600 dark:text-gray-400">{t('import.columns.description')}</p>
              
              {/* Column Suggestions Button */}
              {suggestionsEnabled && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleGetColumnSuggestions}
                  disabled={isLoadingColumnSuggestions || !parseResult?.headers?.length}
                  className="w-full sm:w-auto whitespace-nowrap"
                >
                  {isLoadingColumnSuggestions ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                      <span className="truncate">{t('import.columns.loadingSuggestions', 'Analyse...')}</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                      <span className="truncate">{t('import.columns.suggestMappings', 'Suggestions automatiques')}</span>
                    </>
                  )}
                </Button>
              )}
            </div>

            {/* Suggestions info */}
            {columnSuggestionsApplied && columnSuggestions.length > 0 && (
              <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-3">
                <p className="text-sm text-purple-700 dark:text-purple-300">
                  <span className="font-medium">{columnSuggestions.filter(s => s.suggestedField).length}</span>
                  {' '}{t('import.columns.suggestionsApplied', 'correspondances suggérées')}
                </p>
              </div>
            )}

            {/* Desktop: Table view */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-3 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap">
                      {t('import.columns.sourceColumn')}
                    </th>
                    <th className="px-3 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">
                      {t('import.columns.sampleValues')}
                    </th>
                    <th className="px-3 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap">
                      {t('import.columns.targetField')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                  {parseResult?.headers
                    .map((header, index) => ({ header, index }))
                    .filter(({ header }) => header && header.trim() !== '')
                    .map(({ header, index }) => {
                      const mapping = columnMapping.find((m) => m.sourceColumn === index);
                      const currentField = mapping?.targetField;
                      const sampleValues = parseResult.previewRows.slice(0, 3).map((row) => row[index]).filter(Boolean).join(', ');

                      return (
                        <tr key={index}>
                          <td className="px-3 sm:px-4 py-2 text-sm font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">{header}</td>
                          <td className="px-3 sm:px-4 py-2 text-sm text-gray-500 dark:text-gray-400 max-w-[150px] truncate">{sampleValues}</td>
                          <td className="px-3 sm:px-4 py-2">
                            <select
                              value={currentField || ''}
                              onChange={(e) => handleColumnMappingChange(index, (e.target.value || null) as ImportTargetField | null)}
                              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1 text-sm"
                            >
                              <option value="">{t('import.columns.ignore')}</option>
                              {IMPORT_TARGET_FIELDS
                                .filter((field) => 
                                  field.key === currentField || 
                                  field.key === 'ignore' || 
                                  !usedFields.includes(field.key)
                                )
                                .map((field) => (
                                  <option key={field.key} value={field.key}>
                                    {t(field.labelKey)}
                                    {field.required ? ' *' : ''}
                                  </option>
                                ))}
                            </select>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>

            {/* Mobile: Card view */}
            <div className="sm:hidden space-y-1.5">
              {parseResult?.headers
                .map((header, index) => ({ header, index }))
                .filter(({ header }) => header && header.trim() !== '')
                .map(({ header, index }) => {
                  const mapping = columnMapping.find((m) => m.sourceColumn === index);
                  const currentField = mapping?.targetField;
                  const sampleValues = parseResult.previewRows.slice(0, 2).map((row) => row[index]).filter(Boolean).join(', ');

                  return (
                    <div key={index} className="bg-gray-50 dark:bg-gray-800 rounded p-2">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-gray-900 dark:text-gray-100">{header}</span>
                        {sampleValues && (
                          <span className="text-[10px] text-gray-400 dark:text-gray-500 truncate max-w-[40%] ml-1">
                            {sampleValues}
                          </span>
                        )}
                      </div>
                      <select
                        value={currentField || ''}
                        onChange={(e) => handleColumnMappingChange(index, (e.target.value || null) as ImportTargetField | null)}
                        className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-1.5 py-1 text-xs"
                      >
                        <option value="">{t('import.columns.ignore')}</option>
                        {IMPORT_TARGET_FIELDS
                          .filter((field) => 
                            field.key === currentField || 
                            field.key === 'ignore' || 
                            !usedFields.includes(field.key)
                          )
                          .map((field) => (
                            <option key={field.key} value={field.key}>
                              {t(field.labelKey)}
                              {field.required ? ' *' : ''}
                            </option>
                          ))}
                      </select>
                    </div>
                  );
                })}
            </div>

            <p className="text-xs text-gray-500 dark:text-gray-400">* {t('import.columns.requiredFields')}</p>
          </div>
        );

      case 'categories':
        return (
          <div className="space-y-4">
            {/* Mobile: stacked layout */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <p className="text-sm text-gray-600 dark:text-gray-400">{t('import.categories.description')}</p>
              
              {/* AI Suggestions Button */}
              {suggestionsEnabled && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleGetSuggestions}
                  disabled={isLoadingSuggestions || uniqueCategories.length === 0}
                  className="w-full sm:w-auto whitespace-nowrap"
                >
                  {isLoadingSuggestions ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                      <span className="truncate">{t('import.categories.loadingSuggestions', 'Analyse...')}</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                      <span className="truncate">{t('import.categories.suggestMappings', 'Suggestions automatiques')}</span>
                    </>
                  )}
                </Button>
              )}
            </div>

            {/* Suggestions info */}
            {suggestionsApplied && suggestions.length > 0 && (
              <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-3">
                <p className="text-sm text-purple-700 dark:text-purple-300">
                  <span className="font-medium">{suggestions.filter(s => s.suggestedCategoryId).length}</span>
                  {' '}{t('import.categories.suggestionsApplied', 'correspondances suggérées')}
                  {' '}({suggestions.some(s => s.method === 'ai') 
                    ? t('import.categories.methodAI', 'par IA') 
                    : t('import.categories.methodLevenshtein', 'par similarité')})
                </p>
              </div>
            )}

            {/* Scrollable container for categories */}
            <div className="max-h-[400px] overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
              {/* Desktop view */}
              <div className="hidden sm:block space-y-2 p-2">
                {uniqueCategories.map((sourceValue) => {
                  const mapping = categoryMapping.find((m) => m.sourceValue === sourceValue);
                  const suggestion = suggestions.find((s) => s.sourceCategory === sourceValue);

                  return (
                    <div key={sourceValue} className="flex items-center gap-4 p-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100 min-w-[150px] truncate">{sourceValue}</span>
                      <span className="text-gray-400">→</span>
                      <select
                        value={mapping?.targetCategoryId || ''}
                        onChange={(e) => handleCategoryMappingChange(sourceValue, e.target.value || null)}
                        className="flex-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1 text-sm"
                      >
                        <option value="">{t('import.categories.noMapping')}</option>
                        {sortedCategoriesForDropdown.map((cat) => (
                          <option key={cat.id} value={cat.id}>
                            {cat.displayName}
                          </option>
                        ))}
                      </select>
                      {suggestion && suggestion.suggestedCategoryId && (
                        <span 
                          className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${
                            suggestion.confidence >= 90 
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                              : suggestion.confidence >= 70
                              ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                              : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                          }`}
                          title={suggestion.method === 'ai' ? 'IA' : suggestion.method === 'exact' ? 'Exact' : 'Similarité'}
                        >
                          {suggestion.confidence}%
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Mobile view - Cards */}
              <div className="sm:hidden space-y-1.5 p-1.5">
                {uniqueCategories.map((sourceValue) => {
                  const mapping = categoryMapping.find((m) => m.sourceValue === sourceValue);
                  const suggestion = suggestions.find((s) => s.sourceCategory === sourceValue);

                  return (
                    <div key={sourceValue} className="bg-gray-50 dark:bg-gray-800 rounded p-2">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-gray-900 dark:text-gray-100">{sourceValue}</span>
                        {suggestion && suggestion.suggestedCategoryId && (
                          <span 
                            className={`text-[10px] px-1.5 py-0.5 rounded-full flex-shrink-0 ml-1 ${
                              suggestion.confidence >= 90 
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                : suggestion.confidence >= 70
                                ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                            }`}
                          >
                            {suggestion.confidence}%
                          </span>
                        )}
                      </div>
                      <select
                        value={mapping?.targetCategoryId || ''}
                        onChange={(e) => handleCategoryMappingChange(sourceValue, e.target.value || null)}
                        className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-1.5 py-1 text-xs"
                      >
                        <option value="">{t('import.categories.noMapping')}</option>
                        {sortedCategoriesForDropdown.map((cat) => (
                          <option key={cat.id} value={cat.id}>
                            {cat.displayName}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                })}
              </div>
            </div>
            
            {uniqueCategories.length > 0 && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('import.categories.count', '{{count}} catégorie(s) trouvée(s)').replace('{{count}}', String(uniqueCategories.length))}
              </p>
            )}

            <div className="flex items-center mt-4">
              <input
                type="checkbox"
                id="autoCreateCategories"
                checked={autoCreateCategories}
                onChange={(e) => setAutoCreateCategories(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <label htmlFor="autoCreateCategories" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                {t('import.categories.autoCreate')}
              </label>
            </div>
          </div>
        );

      case 'paymentMethods':
        return (
          <div className="space-y-4">
            {/* Mobile: stacked layout */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <p className="text-sm text-gray-600 dark:text-gray-400">{t('import.paymentMethods.description')}</p>
              
              {/* Payment Method Suggestions Button */}
              {suggestionsEnabled && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleGetPaymentMethodSuggestions}
                  disabled={isLoadingPaymentMethodSuggestions || uniquePaymentMethods.length === 0}
                  className="w-full sm:w-auto whitespace-nowrap"
                >
                  {isLoadingPaymentMethodSuggestions ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                      <span className="truncate">{t('import.paymentMethods.loadingSuggestions', 'Analyse...')}</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                      <span className="truncate">{t('import.paymentMethods.suggestMappings', 'Suggestions automatiques')}</span>
                    </>
                  )}
                </Button>
              )}
            </div>

            {/* Suggestions info */}
            {paymentMethodSuggestionsApplied && paymentMethodSuggestions.length > 0 && (
              <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-3">
                <p className="text-sm text-purple-700 dark:text-purple-300">
                  <span className="font-medium">{paymentMethodSuggestions.filter(s => s.suggestedPaymentMethodId).length}</span>
                  {' '}{t('import.paymentMethods.suggestionsApplied', 'correspondances suggérées')}
                </p>
              </div>
            )}

            {/* Scrollable container for payment methods */}
            <div className="max-h-[400px] overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
              {/* Desktop view */}
              <div className="hidden sm:block space-y-2 p-2">
                {uniquePaymentMethods.map((sourceValue) => {
                  const mapping = paymentMethodMapping.find((m) => m.sourceValue === sourceValue);
                  const suggestion = paymentMethodSuggestions.find((s) => s.sourcePaymentMethod === sourceValue);

                  return (
                    <div key={sourceValue} className="flex items-center gap-4 p-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100 min-w-[150px] truncate">{sourceValue}</span>
                      {suggestion && suggestion.suggestedPaymentMethodId && (
                        <span className={`text-xs px-1.5 py-0.5 rounded flex-shrink-0 ${
                          suggestion.confidence >= 90 
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                            : suggestion.confidence >= 70 
                              ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' 
                              : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                        }`}>
                          {suggestion.confidence}%
                        </span>
                      )}
                      <span className="text-gray-400">→</span>
                      <select
                        value={mapping?.targetPaymentMethodId || ''}
                        onChange={(e) => handlePaymentMethodMappingChange(sourceValue, e.target.value || null)}
                        className="flex-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1 text-sm"
                      >
                        <option value="">{t('import.paymentMethods.noMapping')}</option>
                        {paymentMethods.map((pm) => (
                          <option key={pm.id} value={pm.id}>
                            {pm.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                })}
              </div>

              {/* Mobile view - Cards */}
              <div className="sm:hidden space-y-1.5 p-1.5">
                {uniquePaymentMethods.map((sourceValue) => {
                  const mapping = paymentMethodMapping.find((m) => m.sourceValue === sourceValue);
                  const suggestion = paymentMethodSuggestions.find((s) => s.sourcePaymentMethod === sourceValue);

                  return (
                    <div key={sourceValue} className="bg-gray-50 dark:bg-gray-800 rounded p-2">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-gray-900 dark:text-gray-100">{sourceValue}</span>
                        {suggestion && suggestion.suggestedPaymentMethodId && (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full flex-shrink-0 ml-1 ${
                            suggestion.confidence >= 90 
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                              : suggestion.confidence >= 70 
                                ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' 
                                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                          }`}>
                            {suggestion.confidence}%
                          </span>
                        )}
                      </div>
                      <select
                        value={mapping?.targetPaymentMethodId || ''}
                        onChange={(e) => handlePaymentMethodMappingChange(sourceValue, e.target.value || null)}
                        className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-1.5 py-1 text-xs"
                      >
                        <option value="">{t('import.paymentMethods.noMapping')}</option>
                        {paymentMethods.map((pm) => (
                          <option key={pm.id} value={pm.id}>
                            {pm.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                })}
              </div>
            </div>

            {uniquePaymentMethods.length > 0 && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('import.paymentMethods.count', '{{count}} mode(s) de paiement trouvé(s)').replace('{{count}}', String(uniquePaymentMethods.length))}
              </p>
            )}

            <div className="flex items-center mt-4">
              <input
                type="checkbox"
                id="autoCreatePaymentMethods"
                checked={autoCreatePaymentMethods}
                onChange={(e) => setAutoCreatePaymentMethods(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <label htmlFor="autoCreatePaymentMethods" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                {t('import.paymentMethods.autoCreate')}
              </label>
            </div>
          </div>
        );

      case 'projects':
        return (
          <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">{t('import.projects.description', 'Associez les projets du fichier aux projets Budgira')}</p>

            {/* Scrollable container for projects */}
            <div className="max-h-[400px] overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
              {/* Desktop view */}
              <div className="hidden sm:block space-y-2 p-2">
                {uniqueProjects.map((sourceValue) => {
                  const mapping = projectMapping.find((m) => m.sourceValue === sourceValue);

                  return (
                    <div key={sourceValue} className="flex items-center gap-4 p-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100 min-w-[150px] truncate">{sourceValue}</span>
                      <span className="text-gray-400">→</span>
                      <select
                        value={mapping?.targetProjectId || ''}
                        onChange={(e) => handleProjectMappingChange(sourceValue, e.target.value || null)}
                        className="flex-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1 text-sm"
                      >
                        <option value="">{t('import.projects.noMapping', '-- Aucune correspondance --')}</option>
                        {projects.map((project) => (
                          <option key={project.id} value={project.id}>
                            {project.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                })}
              </div>

              {/* Mobile view - Cards */}
              <div className="sm:hidden space-y-1.5 p-1.5">
                {uniqueProjects.map((sourceValue) => {
                  const mapping = projectMapping.find((m) => m.sourceValue === sourceValue);

                  return (
                    <div key={sourceValue} className="bg-gray-50 dark:bg-gray-800 rounded p-2">
                      <div className="mb-1">
                        <span className="text-xs font-medium text-gray-900 dark:text-gray-100">{sourceValue}</span>
                      </div>
                      <select
                        value={mapping?.targetProjectId || ''}
                        onChange={(e) => handleProjectMappingChange(sourceValue, e.target.value || null)}
                        className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-1.5 py-1 text-xs"
                      >
                        <option value="">{t('import.projects.noMapping', '-- Aucune correspondance --')}</option>
                        {projects.map((project) => (
                          <option key={project.id} value={project.id}>
                            {project.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                })}
              </div>
            </div>

            {uniqueProjects.length > 0 && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('import.projects.count', '{{count}} projet(s) trouvé(s)').replace('{{count}}', String(uniqueProjects.length))}
              </p>
            )}

            <div className="flex items-center mt-4">
              <input
                type="checkbox"
                id="autoCreateProjects"
                checked={autoCreateProjects}
                onChange={(e) => setAutoCreateProjects(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <label htmlFor="autoCreateProjects" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                {t('import.projects.autoCreate', 'Créer automatiquement les projets non mappés')}
              </label>
            </div>
          </div>
        );

      case 'preview':
        if (importResult) {
          return (
            <div className="text-center py-6 sm:py-8">
              <div className={`text-5xl sm:text-6xl mb-4 ${importResult.status === 'completed' ? 'text-green-500' : 'text-red-500'}`}>
                {importResult.status === 'completed' ? '✓' : '✗'}
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                {importResult.status === 'completed' ? t('import.result.success') : t('import.result.failed')}
              </h3>
              <div className="grid grid-cols-3 gap-2 sm:gap-4 max-w-md mx-auto">
                <div className="bg-green-50 dark:bg-green-900/20 p-2 sm:p-4 rounded-lg">
                  <div className="text-xl sm:text-2xl font-bold text-green-600 dark:text-green-400">{importResult.importedCount}</div>
                  <div className="text-xs sm:text-sm text-green-600 dark:text-green-400">{t('import.result.imported')}</div>
                </div>
                <div className="bg-yellow-50 dark:bg-yellow-900/20 p-2 sm:p-4 rounded-lg">
                  <div className="text-xl sm:text-2xl font-bold text-yellow-600 dark:text-yellow-400">{importResult.skippedCount}</div>
                  <div className="text-xs sm:text-sm text-yellow-600 dark:text-yellow-400">{t('import.result.skipped')}</div>
                </div>
                <div className="bg-red-50 dark:bg-red-900/20 p-2 sm:p-4 rounded-lg">
                  <div className="text-xl sm:text-2xl font-bold text-red-600 dark:text-red-400">{importResult.errorCount}</div>
                  <div className="text-xs sm:text-sm text-red-600 dark:text-red-400">{t('import.result.errors')}</div>
                </div>
              </div>

              {/* Skipped operations details */}
              {importResult.skipped && importResult.skipped.length > 0 && (
                <div className="mt-6 max-w-2xl mx-auto">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 text-left">
                    {t('import.result.skippedDetails')}
                  </h4>
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 max-h-48 overflow-y-auto">
                    <table className="w-full text-sm text-left">
                      <thead className="text-xs text-yellow-700 dark:text-yellow-400 uppercase border-b border-yellow-200 dark:border-yellow-700">
                        <tr>
                          <th className="py-1 px-2">{t('import.result.skippedRow')}</th>
                          <th className="py-1 px-2">{t('import.result.skippedDate')}</th>
                          <th className="py-1 px-2">{t('import.result.skippedCounterparty')}</th>
                          <th className="py-1 px-2">{t('import.result.skippedAmount')}</th>
                          <th className="py-1 px-2">{t('import.result.skippedReason')}</th>
                        </tr>
                      </thead>
                      <tbody className="text-yellow-800 dark:text-yellow-300">
                        {importResult.skipped.map((item, idx) => (
                          <tr key={idx} className="border-b border-yellow-100 dark:border-yellow-800 last:border-0">
                            <td className="py-1 px-2">{item.row}</td>
                            <td className="py-1 px-2">{item.data?.date ? formatDate(item.data.date) : '-'}</td>
                            <td className="py-1 px-2 truncate max-w-[150px]" title={item.data?.counterparty}>{item.data?.counterparty || '-'}</td>
                            <td className="py-1 px-2">{item.data?.amount || '-'}</td>
                            <td className="py-1 px-2">
                              {item.reason === 'duplicate' ? t('import.result.reasonDuplicate') : item.reason}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Error operations details */}
              {importResult.errors && importResult.errors.length > 0 && (
                <div className="mt-6 max-w-2xl mx-auto">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 text-left">
                    {t('import.result.errorDetails', 'Détail des erreurs :')}
                  </h4>
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 max-h-48 overflow-y-auto">
                    <table className="w-full text-sm text-left">
                      <thead className="text-xs text-red-700 dark:text-red-400 uppercase border-b border-red-200 dark:border-red-700">
                        <tr>
                          <th className="py-1 px-2">{t('import.result.errorRow', 'Ligne')}</th>
                          <th className="py-1 px-2">{t('import.result.errorMessage', 'Erreur')}</th>
                        </tr>
                      </thead>
                      <tbody className="text-red-800 dark:text-red-300">
                        {importResult.errors.map((item, idx) => (
                          <tr key={idx} className="border-b border-red-100 dark:border-red-800 last:border-0">
                            <td className="py-1 px-2 font-medium">{item.row}</td>
                            <td className="py-1 px-2">{item.message}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="mt-6">
                <Button onClick={() => navigate(`/accounts/${accountId}/operations`)}>{t('import.result.viewOperations')}</Button>
              </div>
            </div>
          );
        }

        return (
          <div className="space-y-4">
            {/* Stats - responsive grid */}
            <div className="grid grid-cols-3 gap-2 sm:gap-4">
              <div className="bg-gray-50 dark:bg-gray-800 p-2 sm:p-4 rounded-lg text-center">
                <div className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-gray-100">{previewStats.totalRows}</div>
                <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">{t('import.preview.totalRows')}</div>
              </div>
              <div className="bg-green-50 dark:bg-green-900/20 p-2 sm:p-4 rounded-lg text-center">
                <div className="text-lg sm:text-2xl font-bold text-green-600 dark:text-green-400">{previewStats.validRows}</div>
                <div className="text-xs sm:text-sm text-green-600 dark:text-green-400">{t('import.preview.validRows')}</div>
              </div>
              <div className="bg-red-50 dark:bg-red-900/20 p-2 sm:p-4 rounded-lg text-center">
                <div className="text-lg sm:text-2xl font-bold text-red-600 dark:text-red-400">{previewStats.invalidRows}</div>
                <div className="text-xs sm:text-sm text-red-600 dark:text-red-400">{t('import.preview.invalidRows')}</div>
              </div>
            </div>

            {/* Duplicate handling */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('import.preview.duplicateHandling')}
              </label>
              <select
                value={duplicateHandling}
                onChange={(e) => setDuplicateHandling(e.target.value as DuplicateHandling)}
                className="w-full sm:w-auto rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
              >
                <option value="skip">{t('import.preview.duplicateSkip')}</option>
                <option value="import">{t('import.preview.duplicateImport')}</option>
              </select>
            </div>

            {/* Preview table - responsive */}
            <div className="border rounded-lg dark:border-gray-600 max-h-96 overflow-auto -mx-4 sm:mx-0">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
                  <tr>
                    <th className="px-2 sm:px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 w-10 sm:w-12">#</th>
                    <th className="px-2 sm:px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 w-20 sm:w-28">{t('import.preview.date')}</th>
                    <th className="px-2 sm:px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 w-20 sm:w-28">{t('import.preview.amount')}</th>
                    <th className="px-2 sm:px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 hidden sm:table-cell">{t('import.preview.counterparty')}</th>
                    <th className="px-2 sm:px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 hidden md:table-cell w-40">{t('import.preview.category')}</th>
                    <th className="px-2 sm:px-3 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 w-10 sm:w-16">{t('import.preview.status')}</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                  {previews.map((preview) => (
                    <tr key={preview.row} className={preview.isValid ? '' : 'bg-red-50 dark:bg-red-900/20'}>
                      <td className="px-2 sm:px-3 py-2 text-xs sm:text-sm text-gray-500 dark:text-gray-400">{preview.row}</td>
                      <td className="px-2 sm:px-3 py-2 text-xs sm:text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap">{preview.paymentDate ? formatDate(preview.paymentDate) : '-'}</td>
                      <td className={`px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-right whitespace-nowrap ${preview.direction === 'credit' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                        {preview.direction === 'debit' ? '-' : '+'}{preview.amount?.toFixed(2)}
                      </td>
                      <td className="px-2 sm:px-3 py-2 text-sm text-gray-900 dark:text-gray-100 max-w-[120px] sm:max-w-xs truncate hidden sm:table-cell" title={preview.counterpartyName}>
                        {preview.counterpartyName}
                      </td>
                      <td className="px-2 sm:px-3 py-2 text-sm text-gray-900 dark:text-gray-100 hidden md:table-cell">
                        {preview.categoryName ? (
                          <span className={`truncate block ${preview.isSuggested ? 'italic text-indigo-600 dark:text-indigo-400' : ''}`} title={preview.categoryName}>
                            {preview.categoryName}
                            {preview.isSuggested && (
                              <span className="ml-1 text-xs" title={t('import.preview.suggestedCategory')}>💡</span>
                            )}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="px-2 sm:px-3 py-2 text-sm text-center">
                        {preview.isValid ? (
                          <span className="text-green-600 dark:text-green-400">✓</span>
                        ) : (
                          <span className="text-red-600 dark:text-red-400" title={preview.validationErrors?.join(', ')}>
                            ✗
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="container mx-auto px-1 sm:px-4 py-3 sm:py-6">
      {/* Header */}
      <div className="mb-3 sm:mb-6">
        <button onClick={() => navigate(`/accounts/${accountId}/operations`)} className="text-indigo-600 dark:text-indigo-400 hover:underline mb-2 flex items-center gap-1">
          ← {t('import.backToOperations')}
        </button>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('import.title')}</h1>
      </div>

      {/* Account Banner */}
      {selectedAccount && (
        <div className="mb-6 bg-gradient-to-r from-indigo-500 to-indigo-600 dark:from-indigo-600 dark:to-indigo-700 rounded-xl p-4 sm:p-5 shadow-lg">
          <div className="flex items-center">
            <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
              </svg>
            </div>
            <div className="ml-3 sm:ml-4">
              <p className="text-white/80 text-xs sm:text-sm">{t('import.accountLabel')}</p>
              <p className="text-white font-bold text-lg sm:text-xl">{selectedAccount.name}</p>
            </div>
          </div>
        </div>
      )}

      {/* Step indicator */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => {
            const isActive = index === currentStepIndex;
            const isCompleted = index < currentStepIndex;

            return (
              <React.Fragment key={step.key}>
                <div className="flex flex-col items-center">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                      ${isCompleted ? 'bg-indigo-600 text-white' : isActive ? 'bg-indigo-100 text-indigo-600 border-2 border-indigo-600 dark:bg-indigo-900 dark:text-indigo-300' : 'bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400'}`}
                  >
                    {isCompleted ? '✓' : index + 1}
                  </div>
                  <span className={`mt-1 text-xs ${isActive ? 'text-indigo-600 dark:text-indigo-400 font-medium' : 'text-gray-500 dark:text-gray-400'} hidden sm:block`}>
                    {t(step.labelKey)}
                  </span>
                </div>
                {index < steps.length - 1 && <div className={`flex-1 h-0.5 mx-2 ${isCompleted ? 'bg-indigo-600' : 'bg-gray-200 dark:bg-gray-700'}`} />}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Error display */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Content card */}
      <Card className="p-2 sm:p-6">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            <span className="mt-4 text-lg font-medium text-gray-700 dark:text-gray-300">
              {currentStep === 'preview' ? t('import.importing') : t('common.loading')}
            </span>
            {currentStep === 'preview' && previewStats.validRows > 1000 && (
              <span className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                {t('import.largeFileWarning', { count: previewStats.validRows })}
              </span>
            )}
          </div>
        ) : (
          renderStepContent()
        )}
      </Card>

      {/* Navigation buttons */}
      {!importResult && (
        <div className="mt-6 flex justify-between">
          <Button variant="secondary" onClick={handleBack} disabled={currentStep === 'upload' || isLoading}>
            {t('common.back')}
          </Button>

          {currentStep === 'preview' ? (
            <Button onClick={handleImport} disabled={isLoading || previewStats.validRows === 0}>
              {t('import.preview.importButton')} ({previewStats.validRows})
            </Button>
          ) : (
            <Button onClick={handleNext} disabled={isLoading || !parseResult}>
              {t('common.next')}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default ImportPage;
